import { Settings } from "../../Settings";
import GridTile from "../grid/GridTile";
import LayerDescription from "../grid/LayerDescription";
import FractionalBrownianMotion from "./FractionalBrownianMotion";
import TerrainLayer from "./TerrainLayer";

// The fbm noise stuff can push the boundaries of the tile layer outside of their configured ranges.
// Use a fudge value to expand the min depth.
const TILE_FUDGE = 60;

export default class BasicTerrainLayer extends TerrainLayer {
    /**
     * @param {LayerDescription} layerDescription
     * @param {FractionalBrownianMotion} fbm 
     * @param {boolean} goldGenerated
     * @param {boolean} rubyGenerated
     */
    constructor(layerDescription, fbm, goldGenerated, rubyGenerated) {
        super();
        
        /**
         * @constant
         * @type {LayerDescription}
         */
        this.layerDescription = layerDescription;
        /**
         * @constant
         * @type {number}
         */
        this.maxTileDepth = layerDescription.maxDepth;
        /**
         * This is really the starting depth, or min depth.
         * @constant
         * @type {number}
         */
        this.minTileDepth = layerDescription.minDepth;

        /**
         * This is really the starting depth, or min depth.
         * @constant
         * @type {number}
         */
        this.adjustedMinTileDepth = this.minTileDepth - TILE_FUDGE;

        // NOTE: The fbm noise is applied to the 'min' tile row (i.e. the top of the terrain layer)

        /**
         * @constant
         * @type {FractionalBrownianMotion}
         */
        this.fbm = fbm;

        /**
         * @constant
         * @type {boolean}
         */
        this.goldGenerated = goldGenerated;
        /**
         * @constant
         * @type {boolean}
         */
        this.rubyGenerated = rubyGenerated;
    }

    /**
     * @override
     * @returns {LayerDescription}
     */
    getLayerDescription() {
        return this.layerDescription;
    }

    /**
     * @private
     * @param {number} tileRow The tile row
     * @returns {boolean}
     */
    isInAdjustedRange(tileRow) {
        return tileRow >= this.adjustedMinTileDepth && tileRow < this.maxTileDepth;
    }

    /**
     * Returns true if at least some part of the grid overlaps with the layer. 
     * Not all tiles in the grid will necessarily be affected by this layer.
     * @override
     * @param {number} minGridTileDepth
     * @param {number} maxGridTileDepth
     * @returns {boolean}
     */
    isLayerForGrid(minGridTileDepth, maxGridTileDepth) {

        if (maxGridTileDepth < this.adjustedMinTileDepth || minGridTileDepth > this.maxTileDepth) {
            return false;
        }

        return true;
    }

    /**
     * @override
     * @param {GridTile} tile The tile
     * @param {number} tileDepth The depth of the tile in tile rows.
     * @returns {boolean}
     */
    isLayerForTile(tile, tileDepth) {
        // If the tile is outside the adjusted range for this layer, then don't bother doing noise calculations
        if (!this.isInAdjustedRange(tileDepth)) {
            return false;
        }

        const origin = tile.origin;

        // Calculate the max layer depth for this terrain column
        // Multiply the noise by some factor (I chose tile size) to increase the effect, and also
        // sort of to put it into tile coordinates.
        const noiseAdjustment = Settings.tile.size * this.fbm.calculateNoise(origin.y, origin.x);
        const adjustedMinLayerDepth = this.minTileDepth + noiseAdjustment;

        // If the tile depth is greater than the ajusted min layer depth, then it is inside this terrain layer.
        return tileDepth >= adjustedMinLayerDepth;
    }

    /**
     * @override
     * @returns {boolean} True if the terrain layer has ore tiles
     */
    isOreTileSupported() {
        return this.layerDescription.oreTileType !== null;
    }

    /**
     * @override
     * @returns {boolean} True if the terrain layer has greater tiles
     */
    isGoldTileSupported() {
        return this.goldGenerated &&  this.layerDescription.goldTileType !== null;
    }

    /**
     * @override
     * @returns {boolean}
     */
    isRubyTileSupported() {
        return this.rubyGenerated && this.layerDescription.rubyTileType !== null;
    }

    /**
     * @override
     * @returns {boolean} True if the terrain layer has rare tiles
     */
    isUpgradeTileSupported() {
        return this.layerDescription.upgradeTileType !== null;
    }

    /**
     * @override
     * @param {GridTile} tile 
     */
    assignTileToOreType(tile) {
        tile.tileInit(false, this.layerDescription.oreTileType);
    }
    /**
     * @override
     * @param {GridTile} tile 
     */
    assignTileToGoldType(tile) {
        tile.tileInit(false, this.layerDescription.goldTileType);
    }
    /**
     * @override
     * @param {GridTile} tile 
     */
    assignTileToUpgradeType(tile) {
        tile.tileInit(false, this.layerDescription.upgradeTileType);
    }
    /**
     * @override
     * @param {GridTile} tile 
     */
    assignTileToRubyType(tile) {
        tile.tileInit(false, this.layerDescription.rubyTileType);
    }

    /**
     * @override
     * @param {GridTile} tile 
      * @param {boolean} open
    */
    assignTileTerrain(tile, open) {
        tile.tileInit(open, this.layerDescription.primaryTileType);
    }
}